import { useCallback, useEffect, useReducer } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { FilterItem } from '~/components/Filter/Accordion';
import type {
  DeviceArch,
  DeviceGroupNew as DeviceGroup,
  DeviceKind,
  DeviceService,
  DeviceStatus,
  TvBrand,
} from '~/generated/graphql';
import { capitalize } from '~/lib/string';
import {
  allUnselectedState,
  getDeviceFiltersParams,
  getDeviceFiltersState,
  type useDeviceFiltersParams,
} from './';

export interface DeviceFilter<T> {
  items: FilterItem<T>[];
  open: boolean;
}

export interface DeviceFilters {
  arch: DeviceFilter<DeviceArch>;
  demo: DeviceFilter<boolean>;
  displayOn: DeviceFilter<boolean>;
  displayServers: DeviceFilter<string>;
  groups: DeviceFilter<number>;
  internal: DeviceFilter<boolean>;
  kind: DeviceFilter<DeviceKind>;
  models: DeviceFilter<string>;
  playerVersions: DeviceFilter<string>;
  services: DeviceFilter<DeviceService>;
  silence: DeviceFilter<boolean>;
  status: DeviceFilter<DeviceStatus>;
  storageBelow: DeviceFilter<number>;
  tags: DeviceFilter<string>;
  temperatureAbove: DeviceFilter<number>;
  tvBrands: DeviceFilter<number>;
  uptimeBelow: DeviceFilter<number>;
}

export interface DevicePanel {
  open: boolean;
}

export interface DeviceFiltersState {
  filters: DeviceFilters;
  panel: DevicePanel;
}

export type DeviceFiltersAction =
  | { type: 'CLEAR_FILTERS' }
  | { type: 'REINITIALIZE'; params: string }
  | ({ type: 'SET_FILTER'; keepPage?: boolean; open?: boolean } & {
      [K in keyof DeviceFilters]: { field: K; items: DeviceFilters[K]['items'] };
    }[keyof DeviceFilters])
  | { type: 'TOGGLE_FILTER'; field: keyof DeviceFilters }
  | { type: 'TOGGLE_PANEL' };

export interface FilterReducerProps {
  params?: ReturnType<typeof useDeviceFiltersParams>;
  deviceTags: readonly string[] | undefined;
  deviceGroups: ReadonlyArray<Pick<DeviceGroup, 'id' | 'name'>> | undefined;
  tvBrands: ReadonlyArray<Pick<TvBrand, 'id' | 'name'>> | undefined;
  displayServers: readonly string[] | undefined;
  models: readonly string[] | undefined;
  playerVersions: readonly string[] | undefined;
}

export const useDeviceFiltersReducer = ({
  params,
  deviceTags,
  deviceGroups,
  tvBrands,
  displayServers,
  models,
  playerVersions,
}: FilterReducerProps) => {
  const [_, setSearchParams] = useSearchParams();

  const reducer = useCallback(
    (state: DeviceFiltersState, action: DeviceFiltersAction): DeviceFiltersState => {
      switch (action.type) {
        case 'CLEAR_FILTERS': {
          if (params) {
            setSearchParams((params) => {
              Object.keys(state.filters).forEach((x) => params.delete(x));
              params.delete('search');
              params.set('page', '1');
              return params;
            });
          }

          return allUnselectedState;
        }

        case 'REINITIALIZE': {
          const urlSearchParams = new URLSearchParams(action.params);
          const p = getDeviceFiltersParams(urlSearchParams);
          if (params) setSearchParams(() => urlSearchParams);
          return getDeviceFiltersState(p);
        }

        case 'SET_FILTER': {
          const {
            field,
            items,
          }: { field: keyof DeviceFilters; items: FilterItem<boolean | string | number>[] } =
            action;
          const { keepPage = false } = action;
          const SCALAR_FIELDS: ReadonlySet<keyof DeviceFilters> = new Set([
            'storageBelow',
            'temperatureAbove',
            'uptimeBelow',
          ]);
          const selected = items.filter((x) => x.selected);
          if (params) {
            setSearchParams(
              (params) => {
                if (selected.length > 0) {
                  const value = SCALAR_FIELDS.has(field)
                    ? String(selected[0].value)
                    : JSON.stringify(selected.map((x) => x.value));
                  params.set(field, value);
                } else {
                  params.delete(field);
                }
                if (!keepPage) params.set('page', '1'); // reset pagination when filters change
                return params;
              },
              { replace: true },
            );
          }
          return {
            ...state,
            filters: {
              ...state.filters,
              [field]: {
                ...state.filters[field],
                ...(action.open != null && { open: action.open }),
                items,
              },
            },
          };
        }

        case 'TOGGLE_FILTER': {
          return {
            ...state,
            filters: {
              ...state.filters,
              [action.field]: {
                ...state.filters[action.field],
                open: !state.filters[action.field].open,
              },
            },
          };
        }

        case 'TOGGLE_PANEL': {
          return {
            ...state,
            panel: { ...state.panel, open: !state.panel.open },
          };
        }

        default: {
          return state;
        }
      }
    },
    [params, setSearchParams],
  );

  const [state, dispatch] = useReducer(reducer, getDeviceFiltersState(params));

  useEffect(() => {
    if (!deviceTags) return;
    if (deviceTags.join() === state.filters.tags.items.map((x) => x.value).join()) return;
    dispatch({
      type: 'SET_FILTER',
      field: 'tags',
      items: deviceTags.map((x) => ({
        label: x,
        selected: params ? params.tags.includes(x) : false,
        value: x,
      })),
      keepPage: true,
      open: params ? params.tags.length > 0 : false,
    });
  }, [deviceTags, params, state.filters.tags.items]);

  useEffect(() => {
    if (!deviceGroups) return;
    if (
      deviceGroups.map((x) => x.id).join() === state.filters.groups.items.map((x) => x.value).join()
    )
      return;
    dispatch({
      type: 'SET_FILTER',
      field: 'groups',
      items: deviceGroups.map((x) => ({
        label: x.name,
        selected: params ? params.groups.includes(x.id) : false,
        value: x.id,
      })),
      keepPage: true,
      open: params ? params.groups.length > 0 : false,
    });
  }, [deviceGroups, params, state.filters.groups.items]);

  useEffect(() => {
    if (!displayServers) return;
    if (displayServers.join() === state.filters.displayServers.items.map((x) => x.value).join())
      return;

    dispatch({
      type: 'SET_FILTER',
      field: 'displayServers',
      items: displayServers.map((x) => ({
        label: capitalize(x),
        selected: params ? params.displayServers.includes(x) : false,
        value: x,
      })),
      keepPage: true,
      open: params ? params.displayServers.length > 0 : false,
    });
  }, [displayServers, params, state.filters.displayServers.items]);

  useEffect(() => {
    if (!models) return;
    if (models.join() === state.filters.models.items.map((x) => x.value).join()) return;

    dispatch({
      type: 'SET_FILTER',
      field: 'models',
      items: models.map((x) => ({
        label: x,
        selected: params ? params.models.includes(x) : false,
        value: x,
      })),
      keepPage: true,
      open: params ? params.models.length > 0 : false,
    });
  }, [models, params, state.filters.models.items]);

  useEffect(() => {
    if (!tvBrands) return;
    if (
      tvBrands.map((x) => x.id).join() === state.filters.tvBrands.items.map((x) => x.value).join()
    )
      return;

    dispatch({
      type: 'SET_FILTER',
      field: 'tvBrands',
      items: tvBrands.map((x) => ({
        label: x.name,
        selected: params ? params.tvBrands.includes(x.id) : false,
        value: x.id,
      })),
      keepPage: true,
      open: params ? params.tvBrands.length > 0 : false,
    });
  }, [params, state.filters.tvBrands.items, tvBrands]);

  useEffect(() => {
    if (!playerVersions) return;
    if (playerVersions.join() === state.filters.playerVersions.items.map((x) => x.value).join())
      return;

    dispatch({
      type: 'SET_FILTER',
      field: 'playerVersions',
      items: playerVersions.map((x) => ({
        label: x,
        selected: params ? params.playerVersions.includes(x) : false,
        value: x,
      })),
      keepPage: true,
      open: params ? params.playerVersions.length > 0 : false,
    });
  }, [playerVersions, params, state.filters.playerVersions.items]);

  return [state, dispatch] as const;
};
